/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import userDetail from "Stores/userDetail";
import CallsUtils from "Utils/CallsUtils";
import countryData from "country-telephone-data";

// ─── helpers ─────────────────────────────────────────────────────────────────

const getCountryFromPhone = phoneNumber => {
  if (!phoneNumber) return null;
  const { allCountries, allCountryCodes, iso2Lookup } = countryData;
  const clean = phoneNumber.replace(/[^\d+]/g, "");
  const prefix = clean.replace(/^\+/, "").substr(0, 4);
  if (allCountryCodes[prefix]) {
    const iso2 = allCountryCodes[prefix][0];
    if (iso2 && iso2Lookup[iso2] !== undefined) return allCountries[iso2Lookup[iso2]];
  }
  let best = { dialCode: "", priority: 10001 };
  allCountries.forEach(c => {
    const n = clean.startsWith("+") ? clean.replace(/^\+/, "") : clean;
    const d = c.dialCode.replace(/^\+/, "");
    if (n.startsWith(d)) {
      if (d.length > best.dialCode.replace(/^\+/, "").length) best = c;
      else if (d.length === best.dialCode.replace(/^\+/, "").length && c.priority < best.priority) best = c;
    }
  });
  return best.priority === 10001 ? null : best;
};

const getInitials = m => {
  const first = (m.firstName || "")[0] || "";
  const last = (m.lastName || "")[0] || "";
  return (first + last).toUpperCase() || m.email?.[0]?.toUpperCase() || "?";
};
const getFullName = m => [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || "";

const formatDateTime = ts => {
  if (!ts) return "";
  const d = new Date(ts);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
};

const exportCSV = (rows, filename) => {
  const esc = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const csv = rows.map(r => r.map(esc).join(",")).join("\n");
  const a = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
    download: filename
  });
  a.click();
};

const DATE_PRESETS = ["Today", "Last 7 days", "Last 30 days", "Custom"];

const getPresetRange = preset => {
  const now = new Date();
  const start = new Date(now);
  if (preset === "Today") { start.setHours(0, 0, 0, 0); }
  else if (preset === "Last 7 days") { start.setDate(now.getDate() - 7); }
  else if (preset === "Last 30 days") { start.setDate(now.getDate() - 30); }
  else return null;
  return { start: start.toISOString().split("T")[0], end: now.toISOString().split("T")[0] };
};

// ─── sub-components ───────────────────────────────────────────────────────────

const SummaryCard = ({ icon, label, value, sub, colorClass }) => (
  <div className="ccuCard">
    <div className={`ccuCard__icon ccuCard__icon--${colorClass}`}>
      <i className="material-icons-outlined">{icon}</i>
    </div>
    <div className="ccuCard__body">
      <div className="ccuCard__label">{label}</div>
      <div className="ccuCard__value">{value}</div>
      {sub && <div className="ccuCard__sub">{sub}</div>}
    </div>
  </div>
);

const SkeletonRow = () => (
  <tr className="ccuTr">
    {[...Array(9)].map((_, i) => (
      <td key={i} className="ccuTd">
        <span className="ccuSkeleton" style={{ width: i === 0 ? 16 : `${60 + (i % 3) * 30}px`, height: 14 }} />
      </td>
    ))}
  </tr>
);

const CallDrawer = ({ call, members, onClose }) => {
  if (!call) return null;
  const member = members.find(m => m.memberId === call.memberId);
  const country = getCountryFromPhone(call.fromNumber);
  return (
    <div className="ccuDrawer__overlay" onClick={onClose}>
      <div className="ccuDrawer" onClick={e => e.stopPropagation()}>
        <div className="ccuDrawer__header">
          <div className="ccuDrawer__title">Call details</div>
          <button className="ccuDrawer__close" onClick={onClose}>✕</button>
        </div>
        <div className="ccuDrawer__body">
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Team member</span>
            <span className="ccuDrawer__val">{member ? getFullName(member) : call.memberId || "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Contact</span>
            <span className="ccuDrawer__val">{call.contactName || "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">From number</span>
            <span className="ccuDrawer__val ccuMono">{call.fromNumber || "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Country</span>
            <span className="ccuDrawer__val">{country?.name || "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Date &amp; time</span>
            <span className="ccuDrawer__val">{formatDateTime(call.createTs)}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Duration</span>
            <span className="ccuDrawer__val">{call.duration ? CallsUtils.secondsToTime(call.duration) : "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Call credits</span>
            <span className="ccuDrawer__val ccuMono">{call.callPrice != null ? `$${parseFloat(call.callPrice).toFixed(4)}` : "—"}</span>
          </div>
          <div className="ccuDrawer__row">
            <span className="ccuDrawer__key">Recording credits</span>
            <span className="ccuDrawer__val ccuMono">{call.recordingPrice != null ? `$${parseFloat(call.recordingPrice).toFixed(4)}` : "—"}</span>
          </div>
          {call.notes && (
            <div className="ccuDrawer__row ccuDrawer__row--notes">
              <span className="ccuDrawer__key">Notes</span>
              <span className="ccuDrawer__val ccuDrawer__val--notes">{call.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const CallCreditUsage = observer(() => {
  const [calls, setCalls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef(null);

  // filter state
  const [datePreset, setDatePreset] = useState("Last 30 days");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  // active (applied) filters
  const [appliedFilters, setAppliedFilters] = useState({
    datePreset: "Last 30 days",
    customStart: "",
    customEnd: "",
    members: [],
    countries: []
  });

  const LIMIT = 25;

  const buildFilter = (f) => {
    const filter = {};
    const range = f.datePreset === "Custom"
      ? (f.customStart || f.customEnd ? { start: f.customStart, end: f.customEnd } : null)
      : getPresetRange(f.datePreset);
    if (range) { filter.startDate = range.start; filter.endDate = range.end; }
    if (f.members?.length) filter.memberIds = f.members;
    if (f.countries?.length) filter.countries = f.countries;
    filter.all = true;
    return filter;
  };

  const fetchCalls = useCallback(async (page, f) => {
    setLoading(true);
    try {
      const res = await makeApi({
        url: URLS.getCallsOverviewData,
        method: "POST",
        data: { pageNo: page, limit: LIMIT, filter: buildFilter(f), totalCount: page === 1 ? 0 : totalCount }
      });
      const data = res?.data || {};
      setCalls(data.calls || []);
      setTotalCount(data.totalCount || 0);
    } catch (e) { console.error("Failed to fetch calls", e); }
    finally { setLoading(false); }
  }, [totalCount]);

  const fetchBalance = async () => {
    try {
      const res = await makeApi({ url: URLS.getCreditsUsage, method: "GET" });
      setBalance(res?.data?.dialerCredits ?? null);
    } catch (e) { console.error("Failed to fetch balance", e); }
  };

  const fetchMembers = async () => {
    try {
      const res = await makeApi({ url: URLS.getTeamActiveList, method: "GET" });
      setMembers(res?.data || []);
    } catch (e) { console.error("Failed to fetch members", e); }
  };

  useEffect(() => {
    fetchBalance();
    fetchMembers();
    fetchCalls(1, appliedFilters);
  }, []);

  // close filter panel on outside click
  useEffect(() => {
    const handler = e => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const applyFilters = () => {
    const f = { datePreset, customStart, customEnd, members: selectedMembers, countries: selectedCountries };
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
    setShowFilter(false);
  };

  const resetFilters = () => {
    setDatePreset("Last 30 days");
    setCustomStart("");
    setCustomEnd("");
    setSelectedMembers([]);
    setSelectedCountries([]);
    const f = { datePreset: "Last 30 days", customStart: "", customEnd: "", members: [], countries: [] };
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
    setShowFilter(false);
  };

  const goToPage = p => { setPageNo(p); fetchCalls(p, appliedFilters); };

  // row selection
  const allChecked = selected.size === calls.length && calls.length > 0;
  const someChecked = selected.size > 0 && selected.size < calls.length;
  const toggleRow = id => setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(calls.map(c => c.id || c._id)));

  // active filter chips
  const chips = [];
  if (appliedFilters.datePreset !== "Last 30 days") chips.push({ key: "date", label: appliedFilters.datePreset === "Custom" ? `${appliedFilters.customStart} → ${appliedFilters.customEnd}` : appliedFilters.datePreset });
  appliedFilters.members.forEach(id => { const m = members.find(x => x.memberId === id); if (m) chips.push({ key: `m_${id}`, label: getFullName(m) }); });
  appliedFilters.countries.forEach(c => chips.push({ key: `c_${c}`, label: c }));

  const removeChip = key => {
    let f = { ...appliedFilters };
    if (key === "date") { f.datePreset = "Last 30 days"; f.customStart = ""; f.customEnd = ""; setDatePreset("Last 30 days"); setCustomStart(""); setCustomEnd(""); }
    else if (key.startsWith("m_")) { const id = key.slice(2); f.members = f.members.filter(x => x !== id); setSelectedMembers(f.members); }
    else if (key.startsWith("c_")) { const c = key.slice(2); f.countries = f.countries.filter(x => x !== c); setSelectedCountries(f.countries); }
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
  };

  // totals
  const totalDuration = calls.reduce((s, c) => s + (c.duration || 0), 0);
  const totalCost = calls.reduce((s, c) => s + (c.callPrice || 0) + (c.recordingPrice || 0), 0);

  const handleExport = () => {
    const rows = selected.size > 0 ? calls.filter(c => selected.has(c.id || c._id)) : calls;
    if (!rows.length) return;
    const headers = ["Member", "Contact", "From Number", "Country", "Date & Time", "Duration", "Call Credits", "Rec. Credits", "Notes"];
    const data = rows.map(c => {
      const member = members.find(m => m.memberId === c.memberId);
      const country = getCountryFromPhone(c.fromNumber);
      return [
        member ? getFullName(member) : c.memberId || "",
        c.contactName || "",
        c.fromNumber || "",
        country?.name || "",
        formatDateTime(c.createTs),
        c.duration ? CallsUtils.secondsToTime(c.duration) : "",
        c.callPrice != null ? `$${parseFloat(c.callPrice).toFixed(4)}` : "",
        c.recordingPrice != null ? `$${parseFloat(c.recordingPrice).toFixed(4)}` : "",
        c.notes || ""
      ];
    });
    exportCSV([headers, ...data], "call_credit_usage.csv");
  };

  // derive unique countries from current calls
  const callCountries = [...new Set(calls.map(c => getCountryFromPhone(c.fromNumber)?.name).filter(Boolean))];

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <div className="ccuPage">
      {activeDrawer && <CallDrawer call={activeDrawer} members={members} onClose={() => setActiveDrawer(null)} />}

      {/* Summary cards */}
      <div className="ccuCards">
        <SummaryCard icon="call" label="Total calls" value={loading ? "—" : totalCount.toLocaleString()} colorClass="calls" />
        <SummaryCard icon="timer" label="Total duration" value={loading ? "—" : CallsUtils.secondsToTime(totalDuration)} colorClass="duration" />
        <SummaryCard icon="account_balance_wallet" label="Credit balance"
          value={balance != null ? `$${parseFloat(balance).toFixed(4)}` : "—"}
          sub={loading ? null : totalCost > 0 ? `$${totalCost.toFixed(4)} used in view` : null}
          colorClass="balance"
        />
      </div>

      {/* Notice bar */}
      <div className="ccuNotice">
        <i className="material-icons-outlined" style={{ fontSize: 16, marginRight: 6 }}>info</i>
        Calls are billed in 1-minute increments. Partial minutes are rounded up.
      </div>

      {/* Toolbar */}
      <div className="ccuToolbar">
        <div className="ccuToolbar__left">
          {chips.map(chip => (
            <span key={chip.key} className="ccuChip">
              {chip.label}
              <button className="ccuChip__dismiss" onClick={() => removeChip(chip.key)}>×</button>
            </span>
          ))}
        </div>
        <div className="ccuToolbar__right">
          <div className="ccuFilterWrap" ref={filterRef}>
            <button className={`ccuBtn ${showFilter ? "ccuBtn--active" : ""}`} onClick={() => setShowFilter(v => !v)}>
              <i className="material-icons-outlined" style={{ fontSize: 14 }}>tune</i>
              Filters
              {chips.length > 0 && <span className="ccuBadge">{chips.length}</span>}
            </button>
            {showFilter && (
              <div className="ccuPanel">
                {/* Date range */}
                <div className="ccuPanel__section">
                  <div className="ccuPanel__label">Date range</div>
                  <div className="ccuPanel__dateTabs">
                    {DATE_PRESETS.map(p => (
                      <button key={p} className={`ccuPanel__dateTab ${datePreset === p ? "ccuPanel__dateTab--active" : ""}`} onClick={() => setDatePreset(p)}>{p}</button>
                    ))}
                  </div>
                  {datePreset === "Custom" && (
                    <div className="ccuPanel__dateInputs">
                      <input type="date" className="ccuPanel__dateInput" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                      <span className="ccuPanel__dateSep">–</span>
                      <input type="date" className="ccuPanel__dateInput" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                    </div>
                  )}
                </div>
                {/* Member filter */}
                {members.length > 0 && (
                  <div className="ccuPanel__section">
                    <div className="ccuPanel__label">Team member</div>
                    <div className="ccuPanel__memberList">
                      {members.map(m => {
                        const checked = selectedMembers.includes(m.memberId);
                        return (
                          <button key={m.memberId} className={`ccuPanel__memberRow ${checked ? "ccuPanel__memberRow--on" : ""}`} onClick={() => setSelectedMembers(prev => checked ? prev.filter(x => x !== m.memberId) : [...prev, m.memberId])}>
                            <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                            <span className="ccuPanel__memberName">{getFullName(m)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Country filter */}
                {callCountries.length > 0 && (
                  <div className="ccuPanel__section">
                    <div className="ccuPanel__label">Country</div>
                    <div className="ccuPanel__memberList">
                      {callCountries.map(c => {
                        const checked = selectedCountries.includes(c);
                        return (
                          <button key={c} className={`ccuPanel__memberRow ${checked ? "ccuPanel__memberRow--on" : ""}`} onClick={() => setSelectedCountries(prev => checked ? prev.filter(x => x !== c) : [...prev, c])}>
                            <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                            <span className="ccuPanel__memberName">{c}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="ccuPanel__footer">
                  <button className="ccuBtn" onClick={resetFilters}>Reset</button>
                  <button className="ccuBtn ccuBtn--primary" onClick={applyFilters}>Apply</button>
                </div>
              </div>
            )}
          </div>
          <button className="ccuBtn" onClick={handleExport} disabled={calls.length === 0}>
            <i className="material-icons-outlined" style={{ fontSize: 14 }}>download</i>
            Export CSV{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="ccuTable__wrap">
        <table className="ccuTable">
          <thead>
            <tr>
              <th className="ccuTh ccuTh--check">
                <input type="checkbox" className="ccuCheckbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked; }} onChange={toggleAll} />
              </th>
              <th className="ccuTh">Team member</th>
              <th className="ccuTh">Contact</th>
              <th className="ccuTh">From number</th>
              <th className="ccuTh">Country</th>
              <th className="ccuTh">Date &amp; time</th>
              <th className="ccuTh ccuTh--right">Duration</th>
              <th className="ccuTh ccuTh--right">Call credits</th>
              <th className="ccuTh ccuTh--right">Rec. credits</th>
              <th className="ccuTh" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(8)].map((_, i) => <SkeletonRow key={i} />)
            ) : calls.length === 0 ? (
              <tr>
                <td colSpan={10} className="ccuTd ccuTd--empty">
                  <i className="material-icons-outlined" style={{ fontSize: 32, color: "#d1d5db", display: "block", marginBottom: 8 }}>phone_missed</i>
                  No calls found for the selected filters
                </td>
              </tr>
            ) : calls.map(c => {
              const id = c.id || c._id;
              const member = members.find(m => m.memberId === c.memberId);
              const country = getCountryFromPhone(c.fromNumber);
              const isSelected = selected.has(id);
              return (
                <tr key={id} className={`ccuTr ${isSelected ? "ccuTr--selected" : ""}`}>
                  <td className="ccuTd ccuTd--check">
                    <input type="checkbox" className="ccuCheckbox" checked={isSelected} onChange={() => toggleRow(id)} />
                  </td>
                  <td className="ccuTd">
                    <div className="ccuMember">
                      <div className="ccuAvatar">{member ? getInitials(member) : "?"}</div>
                      <span className="ccuMember__name">{member ? getFullName(member) : c.memberId || "—"}</span>
                    </div>
                  </td>
                  <td className="ccuTd">
                    <div className="ccuContact">{c.contactName || "—"}</div>
                    {c.contactEmail && <div className="ccuSub">{c.contactEmail}</div>}
                  </td>
                  <td className="ccuTd ccuMono">{c.fromNumber || "—"}</td>
                  <td className="ccuTd">
                    {country ? (
                      <div className="ccuCountry">
                        <span className="ccuCountry__name">{country.name}</span>
                        <span className="ccuCountry__dial">+{country.dialCode}</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="ccuTd ccuDate">{formatDateTime(c.createTs)}</td>
                  <td className="ccuTd ccuTd--right ccuDuration">{c.duration ? CallsUtils.secondsToTime(c.duration) : "—"}</td>
                  <td className="ccuTd ccuTd--right">
                    <span className="ccuCredit">{c.callPrice != null ? `$${parseFloat(c.callPrice).toFixed(4)}` : "—"}</span>
                  </td>
                  <td className="ccuTd ccuTd--right">
                    <span className="ccuCredit ccuCredit--rec">{c.recordingPrice != null ? `$${parseFloat(c.recordingPrice).toFixed(4)}` : "—"}</span>
                  </td>
                  <td className="ccuTd ccuTd--action">
                    {c.notes && (
                      <button className="ccuNotesBtn" title="View notes" onClick={() => setActiveDrawer(c)}>
                        <i className="material-icons-outlined" style={{ fontSize: 14 }}>notes</i>
                      </button>
                    )}
                    <button className="ccuNotesBtn" title="View details" onClick={() => setActiveDrawer(c)}>
                      <i className="material-icons-outlined" style={{ fontSize: 14 }}>open_in_new</i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {!loading && calls.length > 0 && (
            <tfoot>
              <tr className="ccuTfoot">
                <td className="ccuTd" colSpan={6}>
                  {selected.size > 0 ? `${selected.size} row${selected.size > 1 ? "s" : ""} selected` : `${calls.length} of ${totalCount} calls`}
                </td>
                <td className="ccuTd ccuTd--right ccuDuration">{CallsUtils.secondsToTime(totalDuration)}</td>
                <td className="ccuTd ccuTd--right">
                  <span className="ccuCredit">${totalCost.toFixed(4)}</span>
                </td>
                <td className="ccuTd" colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ccuPagination">
          <button className="ccuBtn" disabled={pageNo <= 1} onClick={() => goToPage(pageNo - 1)}>
            <i className="material-icons-outlined" style={{ fontSize: 14 }}>chevron_left</i>
          </button>
          <span className="ccuPagination__info">Page {pageNo} of {totalPages}</span>
          <button className="ccuBtn" disabled={pageNo >= totalPages} onClick={() => goToPage(pageNo + 1)}>
            <i className="material-icons-outlined" style={{ fontSize: 14 }}>chevron_right</i>
          </button>
        </div>
      )}
    </div>
  );
});

export default CallCreditUsage;
